import type { VocabularySnapshotV1 } from "../../versions/v1.ts";
import type { VocabularySnapshotV2 } from "../../versions/v2.ts";
import type { MigrationContext } from "../types.ts";

const V2_SCHEDULER_POLICY_VERSION = 1;

function validLastExerciseId(source: VocabularySnapshotV1, meaningId: string, lastExerciseId: string | null): string | null {
  if (!lastExerciseId) {
    return null;
  }
  const set = source.exerciseSets.find((item) => item.meaningId === meaningId);
  return set?.exercises.some((exercise) => exercise.id === lastExerciseId) ? lastExerciseId : null;
}

/**
 * Adds per-meaning scheduling defaults. Does not infer durable recall from version-1 `practiced`.
 * Drops `cycleId` and `practiced`. Uses one captured `context.migratedAt` for every due date.
 */
export const v1ToV2Step = {
  id: "v1-to-v2-scheduling-defaults",
  fromVersion: 1,
  toVersion: 2,
  description: "Add scheduling fields with due-now defaults.",
  migrate(source: unknown, context: MigrationContext): VocabularySnapshotV2 {
    const snapshot = source as VocabularySnapshotV1;
    const previous = new Map(snapshot.practiceStates.map((state) => [state.meaningId, state]));
    return {
      schemaVersion: 2,
      dataRevision: snapshot.dataRevision,
      schedulerState: {
        schedulerVersion: V2_SCHEDULER_POLICY_VERSION,
        lastPracticedMeaningId: null,
        lastObservedAt: context.migratedAt,
      },
      words: snapshot.words,
      meanings: snapshot.meanings,
      relations: snapshot.relations,
      exerciseSets: snapshot.exerciseSets,
      practiceStates: snapshot.meanings.map((meaning) => ({
        meaningId: meaning.id,
        stage: 0,
        dueAt: context.migratedAt,
        lastExposureAt: null,
        lastAssessmentAt: null,
        lastExerciseId: validLastExerciseId(
          snapshot,
          meaning.id,
          previous.get(meaning.id)?.lastExerciseId ?? null,
        ),
        stateRevision: 0,
        flaggedExerciseIds: [],
      })),
      reviewHistory: [],
      historyTruncated: false,
    };
  },
};
